import React, { useEffect, useState, useCallback } from "react"
import { useParams, Link } from "react-router-dom"
import API from "../../api/axios"
import "./dashboard.css"

const getYouTubeId = (url) => {
  if (!url) return null
  const match = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/)
  return match ? match[1] : null
}

const CoursePlayer = () => {
  const { slug } = useParams()
  const [course, setCourse]         = useState(null)
  const [enrollment, setEnrollment] = useState(null)
  const [progress, setProgress]     = useState([]) // array of completed lessonIds
  const [activeLesson, setActiveLesson] = useState(null)
  const [loading, setLoading]       = useState(true)
  const [marking, setMarking]       = useState(false)
  const [error, setError]           = useState("")

  const fetchData = useCallback(async () => {
    try {
      setLoading(true)
      const courseRes = await API.get(`/courses/${slug}`)
      const courseData = courseRes.data.data
      setCourse(courseData)

      if (courseRes.data.isEnrolled && courseData.id) {
        try {
          const enrollRes = await API.get(`/enrollments/my/${courseData.id}`)
          const enr = enrollRes.data.data
          setEnrollment(enr)
          setProgress((enr.progressItems || []).filter(p => p.completed).map(p => p.lessonId))
          // set first lesson as active
          const firstLesson = courseData.modules?.[0]?.lessons?.[0]
          if (firstLesson) setActiveLesson(firstLesson)
        } catch {}
      } else if (!courseRes.data.isEnrolled) {
        setError("You are not enrolled in this course. Please purchase or enroll first.")
      }
    } catch (err) {
      setError("Course not found or unavailable.")
    } finally {
      setLoading(false)
    }
  }, [slug])

  useEffect(() => { fetchData() }, [fetchData])

  const selectLesson = (lesson) => {
    if (!enrollment) return
    setActiveLesson(lesson)
  }

  const markComplete = async () => {
    if (!activeLesson || !enrollment) return
    setMarking(true)
    try {
      const { data } = await API.patch("/enrollments/progress", {
        courseId:  course.id,
        lessonId:  activeLesson.id,
        watchTime: 0,
      })
      const updated = data.data
      setEnrollment(prev => ({ ...prev, percentComplete: updated.percentComplete, status: updated.status }))
      setProgress((updated.progressItems || []).filter(p => p.completed).map(p => p.lessonId))
    } catch (err) {
      console.error("Failed to mark progress")
    } finally {
      setMarking(false)
    }
  }

  if (loading) {
    return (
      <div className="player-layout" style={{ alignItems: "center", justifyContent: "center" }}>
        <div className="db-loading">
          <i className="fa fa-spinner fa-spin"></i>
          <span>Loading course…</span>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="player-layout" style={{ alignItems: "center", justifyContent: "center" }}>
        <div style={{ textAlign: "center", color: "rgba(255,255,255,0.6)", padding: 32 }}>
          <i className="fa fa-lock" style={{ fontSize: 48, marginBottom: 16, display: "block", color: "#c9a227" }}></i>
          <p>{error}</p>
          <Link to="/courses" style={{ color: "#c9a227", marginTop: 12, display: "inline-block" }}>← Browse Courses</Link>
        </div>
      </div>
    )
  }

  const percent    = enrollment?.percentComplete || 0
  const isCompleted = activeLesson && progress.includes(activeLesson.id)
  const ytId        = activeLesson ? getYouTubeId(activeLesson.videoUrl) : null

  return (
    <div className="player-layout">
      {/* Sidebar */}
      <aside className="player-sidebar">
        <div className="player-sidebar-header">
          <Link to="/dashboard"><i className="fa fa-arrow-left"></i> Back to Dashboard</Link>
          <h3>{course?.title}</h3>
        </div>

        <div className="player-progress-wrap">
          <div className="player-progress-label">
            <span>Course Progress</span>
            <span>{percent}%</span>
          </div>
          <div className="player-progress-bar">
            <div className="player-progress-fill" style={{ width: `${percent}%` }}></div>
          </div>
        </div>

        {course?.modules?.map(mod => (
          <div key={mod.id} className="player-module">
            <div className="player-module-title">{mod.title}</div>
            {mod.lessons?.map(lesson => {
              const isLocked     = !enrollment && !lesson.isFree
              const isLessonDone = progress.includes(lesson.id)
              const isActive     = activeLesson?.id === lesson.id

              return (
                <div
                  key={lesson.id}
                  className={`player-lesson-item ${isActive ? "active" : ""} ${isLessonDone ? "completed" : ""} ${isLocked ? "locked" : ""}`}
                  onClick={() => !isLocked && selectLesson(lesson)}
                >
                  <span className="lesson-icon">
                    {isLocked    ? <i className="fa fa-lock"></i>       :
                     isLessonDone? <i className="fa fa-check-circle"></i> :
                                   <i className="fa fa-play-circle"></i>}
                  </span>
                  <span className="lesson-title">{lesson.title}</span>
                  {lesson.duration > 0 && (
                    <span className="lesson-duration">{Math.round(lesson.duration / 60)}m</span>
                  )}
                </div>
              )
            })}
          </div>
        ))}
      </aside>

      {/* Main player */}
      <div className="player-main">
        {activeLesson ? (
          <>
            <div className="player-video-wrap">
              {ytId ? (
                <iframe
                  src={`https://www.youtube.com/embed/${ytId}?rel=0&modestbranding=1`}
                  title={activeLesson.title}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              ) : activeLesson.videoUrl && activeLesson.videoType === "upload" ? (
                <video src={activeLesson.videoUrl} controls style={{ width: "100%", height: "100%" }} />
              ) : (
                <div className="player-no-video">
                  <i className="fa fa-video-slash"></i>
                  <span>No video available for this lesson</span>
                </div>
              )}
            </div>

            <div className="player-lesson-info">
              <h2>{activeLesson.title}</h2>
              {activeLesson.description && <p>{activeLesson.description}</p>}
              <div className="player-actions">
                {enrollment && (
                  <button
                    className={`btn-mark-complete ${isCompleted ? "done" : ""}`}
                    onClick={markComplete}
                    disabled={isCompleted || marking}
                  >
                    {isCompleted ? (
                      <><i className="fa fa-check"></i> Completed</>
                    ) : marking ? (
                      <><i className="fa fa-spinner fa-spin"></i> Marking…</>
                    ) : (
                      <><i className="fa fa-check-circle"></i> Mark as Complete</>
                    )}
                  </button>
                )}
                {percent === 100 && (
                  <span style={{ color: "#2e7d52", fontWeight: 700, fontSize: 14 }}>
                    🎉 Course Complete! Certificate ID: {enrollment?.certificateId?.slice(0, 8)}…
                  </span>
                )}
              </div>
            </div>
          </>
        ) : (
          <div className="player-no-video" style={{ flex: 1 }}>
            <i className="fa fa-hand-point-left"></i>
            <span>Select a lesson from the sidebar to start</span>
          </div>
        )}
      </div>
    </div>
  )
}

export default CoursePlayer
